import React, { useMemo, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash-es'

const isCollectionToken = /collection:(-?\d+)/

export default function SearchForm({
    spaceId,
    value,
    originalValue,
    suggestions = [],
    downshift: { getComboboxProps },
    onSubmit,
    children
}) {
    const latest = useRef({ value, spaceId, onSubmit, suggestions })
    latest.current = { value, spaceId, onSubmit, suggestions }

    const submit = useCallback(() => {
        const { value, spaceId, onSubmit } = latest.current

        //collection
        if (isCollectionToken.test(value))
            return onSubmit({
                _id: value.match(isCollectionToken)[1],
                search: ''
            })

        if (value == '#') return

        onSubmit({
            _id: spaceId,
            search: value
        })
    }, [])

    const submitBounced = useMemo(
        () => _.debounce(submit, 350, { maxWait: 1000 }),
        [submit]
    )

    const onFormSubmit = useCallback(
        e => {
            e.preventDefault()

            const { suggestions } = latest.current
            if (suggestions.length) return

            submit()
        },
        [submit]
    )

    const onFormBlur = useCallback(() => {
        if (latest.current.value)
            submit()
    }, [submit])

    const prev = useRef({ value, originalValue })
    useEffect(() => {
        const { value: prevValue } = prev.current
        const { value, originalValue, suggestions } = latest.current

        prev.current = { value, originalValue }

        if (prevValue == value ||
            (originalValue || '').trim() == (value || '').trim())
            return

        submitBounced.cancel()

        //submit immediately
        if (!value || value.endsWith(' '))
            return submit()
        //ignore when suggestions showed
        else if (suggestions.length)
            return
        //ignore when token is incomplete
        else if (value.includes(':'))
            return

        submitBounced()
    }, [value, originalValue, submitBounced, submit])

    return (
        <form
            {...getComboboxProps({
                className: 'ui-search-form',
                style: { '--value-ch': (value || '').length + 'ch' },
                onSubmit: onFormSubmit,
                onBlur: onFormBlur
            })}>
            {children}
        </form>
    )
}

SearchForm.propTypes = {
    spaceId: PropTypes.any,
    value: PropTypes.string,
    originalValue: PropTypes.string,
    downshift: PropTypes.shape({
        getComboboxProps: PropTypes.func
    }),
    onSubmit: PropTypes.func
}
